import axios, {AxiosResponse} from 'axios';
import {AuthResponse} from "./Responses/AuthResponse";
import {DataTableRequest} from "./Requests/API/DataTableRequest";
import {LanguagesRequest} from "./Requests/API/LanguagesRequest";
import {TransactionColumn} from "./Responses/TransactionsResponse";
import {WithdrawProvider} from "./WithdrawProvider";
import {APILink} from "./APILink";
import {setupReconnect} from "../hooks/reconnect";

type RequestMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

interface RequestWithCacheOptions {
    data?: any;
    headers?: Record<string, any>;
    params?: Record<string, any>;
    responseType?: string;
    requestKey?: string;
    [key: string]: any;
}

class Cashier {
    private api;

    private token: string | undefined | null;
    private requestsArray: Record<string, Promise<any>> = {};

    constructor() {
        const url = window.location.origin;
        this.api = axios.create({
            baseURL: `${url}`,
            withCredentials: true,
            withXSRFToken: true,
            headers: {
                'Accept': 'application/json'
            }
        });
        this.getCsrfToken();
    }

    private async getCsrfToken(): Promise<void> {
        await this.api.get('sanctum/csrf-cookie');
    }

    async login(email: string, password: string, token: string) {
        const url = APILink.LOGIN;
        return this.requestWithCache(url, 'post', {
            data: {email, password, token}
        }).then((r) => {
            const authResponse = r.data as AuthResponse;
            console.log(authResponse);
            this.token = authResponse.token;
            if (this.token?.length) {
                localStorage.setItem('userToken', this.token.toString());
            }
            if (authResponse.expires_at){
                localStorage.setItem('tokenExpiresAt', authResponse.expires_at.toString());
            }
            setupReconnect();
            return r;
        });
    }

    async reconnect() {
        const url = APILink.RECONNECT;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        }).then((r) => {
            const authResponse = r.data as AuthResponse;
            this.token = authResponse.token;
            if (this.token?.length) {
                localStorage.setItem('userToken', this.token.toString());
            }
            if (authResponse.expires_at){
                localStorage.setItem('tokenExpiresAt', authResponse.expires_at.toString());
            }
            setupReconnect();
            return r;
        });
    }

    async verify2fa(code: string, attempt_email: string) {
        const url = APILink.VERIFY_2FA;
        return this.requestWithCache(url, 'post', {
            data: {code, attempt_email}
        }).then((r) => {
            console.log(r);
            return r;
        });
    }

    async me() {
        const url = APILink.ME;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async brokerMe() {
        const url = APILink.BROKER_ME;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders(2)
        });
    }

    async initPayment() {
        const url = APILink.INIT_PAYMENT;
        return this.requestWithCache(url, 'post', {
            data: {headers: this.prepareHeaders(2)}
        });
    }

    async getTemporaryTransaction<T = AxiosResponse>(transactionID: string | undefined) {
        const url = APILink.PAYMENT.replace('$transactionID', transactionID?.toString() ?? '');
        return this.requestWithCache<T>(url, 'get', {
            headers: this.prepareHeaders(2)
        });
    }

    async payment<T = AxiosResponse>(transactionID: string | undefined, data: object) {
        const url = APILink.PAYMENT.replace('$transactionID', transactionID?.toString() ?? '');
        return this.requestWithCache<T>(url, 'post', {
            data,
            headers: this.prepareHeaders(2)
        });
    }

    async logout() {
        const url = APILink.LOGOUT;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async dashboardStatistic() {
        const url = APILink.DASHBOARD_STATISTIC;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async brokerDashboard(id: number) {
        const url = APILink.BROKER_DASHBOARD;
        return this.requestWithCache(url, 'get', {
            params: {broker: id},
            headers: this.prepareHeaders()
        });
    }

    async getDepositChart(dateFrom: string, dateTo: string, broker: number = 0) {
        const url = APILink.GET_DEPOSIT_CHART;
        let request = {from: dateFrom, to: dateTo};
        if (broker > 0)
            request = {...request, ['broker' as keyof typeof request]: broker};
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getWithdrawChart(dateFrom: string, dateTo: string, broker: number = 0) {
        const url = APILink.GET_WITHDRAW_CHART;
        let request = {from: dateFrom, to: dateTo};
        if (broker > 0)
            request = {...request, ['broker' as keyof typeof request]: broker};
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getLinearChart(dateFrom: string, dateTo: string, broker: number = 0) {
        const url = APILink.GET_LINEAR_CHART;
        let request = {from: dateFrom, to: dateTo};
        if (broker > 0)
            request = {...request, ['broker' as keyof typeof request]: broker};
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getSalesChart(dateFrom: string, dateTo: string, broker: number = 0) {
        const url = APILink.GET_SALES_CHART;
        let request = {from: dateFrom, to: dateTo};
        if (broker > 0)
            request = {...request, ['broker' as keyof typeof request]: broker};
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getSystem() {
        const url = APILink.GET_SYSTEM;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async updateUserMe(data: object) {
        const url = APILink.ME;
        return this.requestWithCache(url, 'patch', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async resetBrokerToken(user: number) {
        const url = APILink.RESET_BROKER_TOKEN.replace('$user', user.toString());
        return this.requestWithCache(url, 'patch', {
            data: {},
            headers: this.prepareHeaders()
        });
    }

    async getReportingSchemaRoles() {
        const url = APILink.GET_REPORTING_SCHEMA_ROLES;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async updateReportingSchemaRoles(request: object) {
        const url = APILink.GET_REPORTING_SCHEMA_ROLES;
        return this.requestWithCache(url, 'put', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async getRoles(request: DataTableRequest) {
        const url = APILink.GET_ROLES;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getRole(role: number) {
        const url = APILink.UPDATE_ROLE.replace('$role', role.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async createRole(data: object) {
        const url = APILink.GET_ROLES;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateRole(role: number, data: object) {
        const url = APILink.UPDATE_ROLE.replace('$role', role.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async deleteRole(role: number, assign?: string) {
        const url = APILink.UPDATE_ROLE.replace('$role', role.toString());
        const data = assign ? {assign} : {};
        return this.requestWithCache(url, 'delete', {
            headers: this.prepareHeaders(),
            data
        });
    }

    async getLanguages(request: LanguagesRequest) {
        const url = APILink.GET_LANGUAGES;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getLanguage(language: number) {
        const url = APILink.GET_LANGUAGE.replace('$language', language.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async createLanguage(data: object) {
        const url = APILink.GET_LANGUAGES;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateLanguage(language: number, data: object) {
        const url = APILink.GET_LANGUAGE.replace('$language', language.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async deleteLanguage(language: number) {
        const url = APILink.GET_LANGUAGE.replace('$language', language.toString());
        return this.requestWithCache(url, 'delete', {
            headers: this.prepareHeaders()
        });
    }

    async getCountries(request: DataTableRequest) {
        const url = APILink.GET_COUNTRIES;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }


    async getCountry(country: number) {
        const url = APILink.GET_COUNTRY.replace('$country', country.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async createCountry(data: object) {
        const url = APILink.GET_COUNTRIES;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateCountry(country: number, data: object) {
        const url = APILink.GET_COUNTRY.replace('$country', country.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async deleteCountry(country: number) {
        const url = APILink.GET_COUNTRY.replace('$country', country.toString());
        return this.requestWithCache(url, 'delete', {
            headers: this.prepareHeaders()
        });
    }

    async getCurrencies(request: DataTableRequest) {
        const url = APILink.GET_CURRENCIES;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getCurrency(currency: number) {
        const url = APILink.GET_CURRENCY.replace('$currency', currency.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async createCurrency(data: object) {
        const url = APILink.GET_COUNTRIES;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateCurrency(currency: number, data: object) {
        const url = APILink.GET_CURRENCY.replace('$currency', currency.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async deleteCurrency(currency: number) {
        const url = APILink.GET_CURRENCY.replace('$currency', currency.toString());
        return this.requestWithCache(url, 'delete', {
            headers: this.prepareHeaders()
        });
    }

    async getRegions(request: DataTableRequest) {
        const url = APILink.GET_REGIONS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getRegionsShortList() {
        const url = APILink.GET_REGIONS_SHORT_LIST;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getRegion(region: number) {
        const url = APILink.GET_REGION.replace('$region', region.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async createRegion(data: object) {
        const url = APILink.GET_REGIONS;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateRegion(region: number, data: object) {
        const url = APILink.GET_REGION.replace('$region', region.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async deleteRegion(region: number) {
        const url = APILink.GET_REGION.replace('$region', region.toString());
        return this.requestWithCache(url, 'delete', {
            headers: this.prepareHeaders()
        });
    }


    async getRoleLimits(limit: number) {
        const url = APILink.GET_ROLE_LIMITS.replace('$limit', limit.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getPayoutLimits(request: object) {
        const url = APILink.GET_PAYOUT_LIMITS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getPayoutLimitsData(request: object) {
        const url = APILink.GET_PAYOUT_LIMITS_DATA;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getPayoutLimitCurrencies() {
        const url = APILink.GET_PAYOUT_LIMIT_CURRENCIES;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async createPayoutLimit(data: object) {
        const url = APILink.GET_PAYOUT_LIMITS;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updatePayoutLimit(limit: number, data: object) {
        const url = APILink.UPDATE_PAYOUT_LIMIT.replace('$limit', limit.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async deletePayoutLimit(limit: number) {
        const url = APILink.UPDATE_PAYOUT_LIMIT.replace('$limit', limit.toString());
        return this.requestWithCache(url, 'delete', {
            headers: this.prepareHeaders()
        });
    }

    async getLogs(request: object) {
        const url = APILink.GET_LOGS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getLogsActivities() {
        const url = APILink.GET_LOGS_ACTIVITIES;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getUsersList(all?: number) {
        const url = APILink.GET_USERS_LIST;
        return this.requestWithCache(url, 'get', {
            params: {all: all ?? 0},
            headers: this.prepareHeaders()
        });
    }

    async getPermissions() {
        const url = APILink.GET_PERMISSIONS;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getShortRoles() {
        const url = APILink.GET_SHORT_ROLES;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getAllowedRoles() {
        const url = APILink.GET_ALLOWED_ROLES;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getUsers(request: object) {
        const url = APILink.GET_USERS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async deleteUser(user: number) {
        const url = APILink.GET_USER.replace('$user', user.toString());
        return this.requestWithCache(url, 'delete', {
            headers: this.prepareHeaders()
        });
    }

    async getUser(user: number) {
        const url = APILink.GET_USER.replace('$user', user.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async createUser(data: object) {
        const url = APILink.GET_USERS;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateUser(user: number, data: object) {
        const url = APILink.GET_USER.replace('$user', user.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async uploadAvatar(data: object) {
        const url = APILink.UPLOAD_AVATAR;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async switch2FA(password: string) {
        const url = APILink.SWITCH_2FA;
        return this.requestWithCache(url, 'post', {
            data: {password},
            headers: this.prepareHeaders()
        });
    }

    async confirm2FA(code: string) {
        const url = APILink.CONFIRM_2FA;
        return this.requestWithCache(url, 'post', {
            data: {code},
            headers: this.prepareHeaders()
        });
    }

    async reset2FA(id: number) {
        const url = APILink.RESET_2FA.replace('$id', id.toString());
        return this.requestWithCache(url, 'post', {
            data: {},
            headers: this.prepareHeaders()
        });
    }

    async setWire(data: object) {
        const url = APILink.SET_WIRE;
        return this.requestWithCache(url, 'patch', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async setBrokerLinks(data: object) {
        const url = APILink.SET_BROKER_LINKS;
        return this.requestWithCache(url, 'patch', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async getProviders(request: object) {
        const url = APILink.GET_PROVIDERS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async setBulkDirectAction(all: boolean, ids: number[], direct_redirect: boolean) {
        const url = APILink.UPDATE_BULK_PROVIDERS;
        return this.requestWithCache(url, 'patch', {
            data: {all, ids, direct_redirect},
            headers: this.prepareHeaders()
        });
    }

    async getProvider(provider: number) {
        const url = APILink.GET_PROVIDER.replace('$provider', provider.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async updateProvider(provider: number, data: object) {
        const url = APILink.GET_PROVIDER.replace('$provider', provider.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateProviderCredentialsInputs(provider: number, data: object) {
        const url = APILink.UPDATE_PROVIDER_CREDENTIALS_INPUTS.replace('$provider', provider.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateProviderClientInputs(provider: number, data: object) {
        const url = APILink.UPDATE_PROVIDER_CLIENT_INPUTS.replace('$provider', provider.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async getPaymentMethods(request: object) {
        const url = APILink.GET_PAYMENT_METHODS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async bulkSetStatusPaymentMethods(all: boolean, ids: number[], status: number) {
        const url = APILink.BULK_PAYMENT_METHODS_STATUS;
        return this.requestWithCache(url, 'patch', {
            data: {all, ids, status},
            headers: this.prepareHeaders()
        });
    }

    async bulkSetTypePaymentMethods(all: boolean, ids: number[], type: number) {
        const url = APILink.BULK_PAYMENT_METHODS_TYPE;
        return this.requestWithCache(url, 'patch', {
            data: {all, ids, type},
            headers: this.prepareHeaders()
        });
    }

    async downloadPaymentMethodsReport(request: object) {
        return this.api.get(`/api/v1/payment-methods/download`, {
            params: request,
            responseType: 'blob',
            headers: this.prepareHeaders()
        });
    }

    async bulkSetProviderPaymentMethods(all: boolean, ids: number[], provider: number) {
        const url = APILink.BULK_PAYMENT_METHODS_PROVIDER;
        return this.requestWithCache(url, 'patch', {
            data: {all, ids, provider},
            headers: this.prepareHeaders()
        });
    }

    async bulkSetCountriesPaymentMethods(all: boolean, ids: number[], countries: number[]) {
        const url = APILink.BULK_PAYMENT_METHODS_COUNTRIES;
        return this.requestWithCache(url, 'patch', {
            data: {all, ids, countries},
            headers: this.prepareHeaders()
        });
    }

    async bulkSetCurrenciesPaymentMethods(all: boolean, ids: number[], currencies: number[]) {
        const url = APILink.BULK_PAYMENT_METHODS_CURRENCIES;
        return this.requestWithCache(url, 'patch', {
            data: {all, ids, currencies},
            headers: this.prepareHeaders()
        });
    }

    async bulkSetOpenInNewTabPaymentMethods(all: boolean, ids: number[], new_tab: boolean) {
        const url = APILink.BULK_PAYMENT_METHODS_OPEN;
        return this.requestWithCache(url, 'patch', {
            data: {all, ids, new_tab},
            headers: this.prepareHeaders()
        });
    }

    async getPaymentMethodTypes() {
        const url = APILink.GET_PAYMENT_METHOD_TYPES;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getPaymentMethod(country: number) {
        const url = APILink.GET_PAYMENT_METHOD.replace('$country', country.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async createPaymentMethod(data: object) {
        const url = APILink.GET_PAYMENT_METHODS;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updatePaymentMethod(paymentMethod: number, data: object) {
        const url = APILink.UPDATE_PAYMENT_METHOD.replace('$paymentMethod', paymentMethod.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async deletePaymentMethod(paymentMethod: number) {
        const url = APILink.UPDATE_PAYMENT_METHOD.replace('$paymentMethod', paymentMethod.toString());
        return this.requestWithCache(url, 'delete', {
            headers: this.prepareHeaders()
        });
    }

    async getCountriesShortList() {
        const url = APILink.GET_COUNTRIES_SHORT_LIST;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getBrokerPaymentMethods(user: number) {
        const url = APILink.GET_BROKER_PAYMENT_METHODS.replace('$user', user.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async setSortingOrder(request: object) {
        const url = APILink.SET_SORTING_ORDER;
        return this.requestWithCache(url, 'patch', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async switchBrokerPaymentMethodStatus(user: number, method: number, status: boolean) {
        const url = APILink.SWITCH_BROKER_PAYMENT_METHOD_STATUS.replace('$user', user.toString()).replace('$method', method.toString());
        return this.requestWithCache(url, 'patch', {
            data: {status},
            headers: this.prepareHeaders()
        });
    }

    async getBrokerPaymentMethodCredentials(brokerPaymentMethod: number) {
        const url = APILink.GET_BROKER_PAYMENT_METHOD_CREDENTIALS.replace('$brokerPaymentMethod', brokerPaymentMethod.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async updateBrokerPaymentMethodCredentials(brokerPaymentMethod: number, data: object) {
        const url = APILink.GET_BROKER_PAYMENT_METHOD_CREDENTIALS.replace('$brokerPaymentMethod', brokerPaymentMethod.toString());
        return this.requestWithCache(url, 'patch', {
            data,
            headers: {
                ...this.prepareHeaders(), 'Content-Type': 'application/x-www-form-urlencoded'
            }
        });
    }

    async getBrokerPaymentMethodCountries(brokerPaymentMethod: number) {
        const url = APILink.GET_BROKER_PAYMENT_METHOD_COUNTRIES.replace('$brokerPaymentMethod', brokerPaymentMethod.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async updateBrokerPaymentMethodCountries(brokerPaymentMethod: number, data: object) {
        const url = APILink.GET_BROKER_PAYMENT_METHOD_COUNTRIES.replace('$brokerPaymentMethod', brokerPaymentMethod.toString());
        return this.requestWithCache(url, 'patch', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async getBrokerPaymentMethodCashier(brokerPaymentMethod: number) {
        const url = APILink.GET_BROKER_PAYMENT_METHOD_CASHIER.replace('$brokerPaymentMethod', brokerPaymentMethod.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getBrokerPaymentMethodBanks(brokerPaymentMethod: number) {
        const url = APILink.GET_BROKER_PAYMENT_METHOD_BANKS.replace('$brokerPaymentMethod', brokerPaymentMethod.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async updateBrokerPaymentMethodCashier(brokerPaymentMethod: number, data: object) {
        const url = APILink.GET_BROKER_PAYMENT_METHOD_CASHIER.replace('$brokerPaymentMethod', brokerPaymentMethod.toString());
        return this.requestWithCache(url, 'patch', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateBrokerPaymentMethodBanks(brokerPaymentMethod: number, data: object) {
        const url = APILink.GET_BROKER_PAYMENT_METHOD_BANKS.replace('$brokerPaymentMethod', brokerPaymentMethod.toString());
        return this.requestWithCache(url, 'patch', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updatePassword(data: object) {
        const url = APILink.UPDATE_PASSWORD;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async getDepositTransactions(request: object) {
        const url = APILink.GET_DEPOSIT_TRANSACTIONS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getBrokersList() {
        const url = APILink.GET_BROKERS_LIST;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getPaymentMethodsList() {
        const url = APILink.GET_PAYMENT_METHODS_LIST;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async resendCallback(id: number) {
        const url = APILink.RESEND_CALLBACK.replace('$id', id.toString());
        return this.requestWithCache(url, 'put', {
            data: {},
            headers: this.prepareHeaders()
        });
    }

    async getCallbacks(request: object) {
        const url = APILink.GET_CALLBACKS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"', '').replaceAll('{', '').replaceAll('}', '').replaceAll(':', '=').replaceAll(',', '&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async restartCallback(id: number) {
        const url = APILink.RESTART_CALLBACK.replace('$id', id.toString());
        return this.requestWithCache(url, 'post', {
            data: {},
            headers: this.prepareHeaders()
        });
    }

    async restartCallbacksQueue() {
        const url = APILink.RESTART_CALLBACKS_QUEUE;
        return this.requestWithCache(url, 'post', {
            data: {},
            headers: this.prepareHeaders()
        });
    }

    async getTransactionColumns(typeId: number) {
        const url = APILink.GET_TRANSACTION_COLUMNS;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders(),
            params: {type_id: typeId},
            requestKey: `get${url}?type_id=${typeId}`
        });
    }

    async updateTransactionColumns(columns: TransactionColumn[], typeId: number) {
        const url = APILink.GET_TRANSACTION_COLUMNS;
        return this.requestWithCache(url, 'put', {
            data: {columns, type_id: typeId},
            headers: this.prepareHeaders(),
            requestKey: `put${url}?type_id=${typeId}`
        });
    }

    async declineConfirmation(confirmation: number) {
        const url = APILink.DECLINE_CONFIRMATION.replace('$confirmation', confirmation.toString());
        return this.requestWithCache(url, 'put', {
            data: {},
            headers: this.prepareHeaders()
        });
    }

    async approveConfirmation(confirmation: number) {
        const url = APILink.APPROVE_CONFIRMATION.replace('$confirmation', confirmation.toString());
        return this.requestWithCache(url, 'put', {
            data: {},
            headers: this.prepareHeaders()
        });
    }

    async shareDepositReport(request: object) {
        const url = APILink.SHARE_DEPOSIT_REPORT;
        return this.requestWithCache(url, 'put', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async getDepositTransactionDetails(id: number) {
        const url = APILink.GET_DEPOSIT_TRANSACTION_DETAILS.replace('$id', id.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getWithdrawTransactionDetails(id: number) {
        const url = APILink.GET_WITHDRAW_TRANSACTION_DETAILS.replace('$id', id.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getWithdrawTransactions(request: object) {
        const url = APILink.GET_WITHDRAW_TRANSACTIONS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getWithdrawProviders() {
        const url = APILink.GET_WITHDRAW_PROVIDERS;
        const name = `get${url}}`;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async shareWithdrawReport(request: object) {
        const url = APILink.SHARE_WITHDRAW_REPORT;
        return this.requestWithCache(url, 'put', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async getReportingTypes() {
        const url = APILink.GET_REPORTING_TYPES;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getReportingSchedules() {
        const url = APILink.GET_REPORTING_SCHEDULES;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getReportingSubscriptions(request: object) {
        const url = APILink.GET_REPORTING_SUBSCRIPTIONS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async createReportingSubscription(data: object) {
        const url = APILink.GET_REPORTING_SUBSCRIPTIONS;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateReportingSubscription(subscription: number, data: object) {
        const url = APILink.UPDATE_REPORTING_SUBSCRIPTION.replace('$subscription', subscription.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async deleteReportingSubscription(subscription: number) {
        const url = APILink.UPDATE_REPORTING_SUBSCRIPTION.replace('$subscription', subscription.toString());
        return this.requestWithCache(url, 'delete', {
            headers: this.prepareHeaders()
        });
    }

    async updateReportingSubscriptionStatus(subscription: number, status: boolean) {
        const url = APILink.UPDATE_REPORTING_SUBSCRIPTION_STATUS.replace('$subscription', subscription.toString());
        return this.requestWithCache(url, 'patch', {
            data: {status},
            headers: this.prepareHeaders()
        });
    }

    async getReportingSubscriptionJobs(subscription: number, request: object) {
        const url = APILink.GET_REPORTING_SUBSCRIPTION_JOBS.replace('$subscription', subscription.toString());
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getPaymentMethodCurrenciesList() {
        const url = APILink.GET_PAYMENT_METHOD_CURRENCIES_LIST;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getBrokerEnabledMethods(user: number) {
        const url = APILink.GET_BROKER_ENABLED_METHODS.replace('$user', user.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async emulatePayment(request: object) {
        const url = APILink.EMULATE_PAYMENT;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async makePayment(request: object, authToken: string) {
        const url = APILink.INIT_PAYMENT;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: {
                Authorization: 'Bearer ' + authToken,
                Accept: 'application/json'
            }
        });
    }

    async getWithdrawBrokersList() {
        const url = APILink.GET_WITHDRAW_BROKERS_LIST;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getPayablBanks() {
        const url = APILink.GET_PAYABL_BANKS;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getWallettecMethods(broker: number, provider: string) {
        const url = APILink.GET_WALLETTEC_METHODS;
        return this.requestWithCache(url, 'get', {
            params: {broker, provider},
            headers: this.prepareHeaders()
        });
    }

    async getWallettecBanks(phone: string, id: number) {
        const url = APILink.GET_WALLETTEC_BANKS;
        return this.requestWithCache(url, 'get', {
            params: {phone, id},
            headers: this.prepareHeaders()
        });
    }

    async searchWithdrawClient(query: string, broker: number) {
        const url = APILink.SEARCH_WITHDRAW_CLIENT;
        return this.requestWithCache(url, 'post', {
            data: {query, broker},
            headers: this.prepareHeaders()
        });
    }

    async makeSafeChargeWithdraw(request: object) {
        const url = APILink.MAKE_SAFECHARGE_WITHDRAW;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async makeBVNKWithdraw(request: object) {
        const url = APILink.MAKE_BVNK_WITHDRAW;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async makePayablWithdraw(request: object) {
        const url = APILink.MAKE_PAYABL_WITHDRAW;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async makeMonetixWithdraw(request: object) {
        const url = APILink.MAKE_MONETIX_WITHDRAW;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async makeWallettecWithdraw(request: object) {
        const url = APILink.MAKE_WALLETTEC_WITHDRAW;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async makeSkrillWithdraw(request: object) {
        const url = APILink.MAKE_SKRILL_WITHDRAW;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async makeLimePayWithdraw(request: object) {
        const url = APILink.MAKE_SKRILL_WITHDRAW;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async makeB2BinPayWithdraw(request: object) {
        const url = APILink.MAKE_B2BINPAY_WITHDRAW;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async makeAwePayWithdraw(request: object) {
        const url = APILink.MAKE_AWEPAY_WITHDRAW;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async makeUniPaymentPayWithdraw(request: object) {
        const url = APILink.MAKE_UNIPAYMENT_WITHDRAW;
        return this.requestWithCache(url, 'post', {
            data: request,
            headers: this.prepareHeaders()
        });
    }

    async getWithdrawCountries(broker: number, provider: WithdrawProvider) {
        const url = APILink.GET_WITHDRAW_COUNTRIES;
        return this.requestWithCache(url, 'get', {
            params: {broker, provider},
            headers: this.prepareHeaders()
        });
    }

    async getWithdrawTransaction(transaction: number) {
        const url = APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', transaction.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getSkrillCurrencies(broker: number) {
        const url = APILink.GET_SKRILL_CURRENCIES.replace('$broker', broker.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getAwePayBanks() {
        const url = APILink.GET_AWEPAY_BANKS;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getB2BinPayWallets(broker: number) {
        const url = APILink.GET_B2BINPAY_WALLETS.replace('$broker', broker.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getBVNKWallets(broker: number) {
        const url = APILink.GET_BVNK_WALLETS.replace('$broker', broker.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getConfirmations(request: object) {
        const url = APILink.GET_CONFIRMATIONS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getConfirmationsFilters() {
        const url = APILink.GET_CONFIRMATIONS_FILTERS;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getTranslations(request: object) {
        const url = APILink.GET_TRANSLATIONS;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async downloadTranslations() {
        const url = APILink.DOWNLOAD_TRANSLATIONS;
        return this.requestWithCache(url, 'get', {
            responseType: 'blob',
            headers: this.prepareHeaders()
        });
    }

    async getSources(request: DataTableRequest) {
        const url = APILink.GET_SOURCES;
        const name = `get${url}_${JSON.stringify(request).replaceAll('"','').replaceAll('{','').replaceAll('}','').replaceAll(':','=').replaceAll(',','&')}`;
        return this.requestWithCache(url, 'get', {
            params: request,
            headers: this.prepareHeaders(),
            requestKey: name
        });
    }

    async getShortSources() {
        const url = APILink.GET_SHORT_SOURCES;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async getSource(source: number) {
        const url = APILink.GET_SOURCE.replace('$source', source.toString());
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders()
        });
    }

    async createSource(data: object) {
        const url = APILink.GET_SOURCES;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async updateSource(source: number, data: object) {
        const url = APILink.GET_SOURCE.replace('$source', source.toString());
        return this.requestWithCache(url, 'put', {
            data,
            headers: this.prepareHeaders()
        });
    }

    async deleteSource(source: number) {
        const url = APILink.GET_SOURCE.replace('$source', source.toString());
        return this.requestWithCache(url, 'delete', {
            headers: this.prepareHeaders()
        });
    }

    async globalSearch(query: string) {
        const url = APILink.GLOBAL_SEARCH;
        return this.requestWithCache(url, 'get', {
            params: {query},
            headers: this.prepareHeaders()
        });
    }

    async updateEncryptionKey() {
        const url = APILink.UPDATE_ENCRYPTION_KEY;
        return this.requestWithCache(url, 'put', {
            data: {},
            headers: this.prepareHeaders()
        });
    }

    async uploadTranslations(file: any) {
        const url = APILink.UPLOAD_TRANSLATIONS;
        const formData = new FormData();
        formData.append('file', file);
        return this.requestWithCache(url, 'post', {
            data: formData,
            headers: {
                ...this.prepareHeaders(),
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    async downloadDepositTransactions(request: object) {
        return this.api.get(`/api/v1/reporting/deposit/download`, {
            params: request,
            responseType: 'blob',
            headers: this.prepareHeaders()
        });
    }

    async downloadWithdrawTransactions(request: object) {
        return this.api.get(`/api/v1/reporting/withdraw/download`, {
            params: request,
            responseType: 'blob',
            headers: this.prepareHeaders()
        });
    }

    async getPasskeyOptions() {
        const url = APILink.GET_PASSKEY_OPTIONS;
        return this.requestWithCache(url, 'get', {
            headers: this.prepareHeaders(),
            credentials: 'include'
        });
    }

    async getAuthPasskeyOptions(email: string) {
        const url = APILink.GET_PASSKEY_LOGIN_OPTIONS;
        return this.requestWithCache(`${url}?email=${email}`, 'get', {
            headers: this.prepareHeaders(),
            credentials: 'include'
        });
    }

    async createPasskey(data: object) {
        const url = APILink.REGISTER_PASSKEY;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders(),
            credentials: 'include'
        });
    }

    async passkeyLogin(data: object) {
        const url = APILink.PASSKEY_LOGIN;
        return this.requestWithCache(url, 'post', {
            data,
            headers: this.prepareHeaders(),
            credentials: 'include'
        }).then((r) => {
            const authResponse = r.data as AuthResponse;
            console.log(authResponse);
            this.token = authResponse.token;
            if (this.token?.length) {
                localStorage.setItem('userToken', this.token.toString());
            }
            if (authResponse.expires_at){
                localStorage.setItem('tokenExpiresAt', authResponse.expires_at.toString());
            }
            setupReconnect();
            return r;
        });
    }

    private requestWithCache<T = AxiosResponse>(url: string, method: RequestMethod, options: RequestWithCacheOptions = {}) {
        const {requestKey, data, ...config} = options;
        const cacheKey = requestKey ?? `${method}${url}`;

        if (typeof this.requestsArray[cacheKey] === 'undefined') {
            const axiosConfig: Record<string, any> = {...config};

            if (method === 'delete' && typeof data !== 'undefined') {
                axiosConfig['data'] = data;
            }

            const request = ['get', 'delete'].includes(method)
                ? this.api[method](url, axiosConfig)
                : this.api[method](url, data, axiosConfig);

            this.requestsArray[cacheKey] = request.finally(() => {
                this.deleteFromRequest(cacheKey);
            });
        }

        return this.requestsArray[cacheKey] as Promise<T>;
    }

    private prepareHeaders(withToken = 1) {
        const headers = {};

        if (withToken) {
            const token = withToken === 1 ? this.getUserToken() : this.getBrokerToken();
            if (typeof token !== "undefined" && token !== null) {
                headers['Authorization'] = "Bearer " + token;
                headers['Accept'] = 'application/json';
            }
        }

        return headers
    }

    private getUserToken() {
        return localStorage.getItem('userToken') ?? this.token;
    }

    private getBrokerToken() {
        return localStorage.getItem('brokerToken') ?? this.token;
    }

    private deleteFromRequest(url: string) {
        setTimeout(() => {
            if (typeof this.requestsArray[url] !== 'undefined')
                delete this.requestsArray[url];
        }, 1000)
    }

    removeURL(url: string) {
        delete this.requestsArray[url];
    }

}

export const API = new Cashier();
